/* begin general import */
import { Download16 } from "@carbon/icons-react";
import { PaginationProps, Spin } from "antd";
import { ColumnProps } from "antd/lib/table";
import PageHeader from "components/PageHeader/PageHeader";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  AdvanceIdFilterMaster,
  Button,
  OneLineText,
  Pagination,
  TagFilter,
} from "react3l-ui-library";
import { filterService } from "services/page-services/filter-service";
import { listService } from "services/page-services/list-service";
import { queryStringService } from "services/page-services/query-string-service";
import {
  getAntOrderType,
  tableService,
} from "services/page-services/table-service";
import nameof from "ts-nameof.macro";
import { IdFilter } from "react3l-advanced-filters";
import { renderMasterIndex } from "helpers/table";
import { importExportService } from "services/page-services/import-export-service";
import { Major, MajorFilter } from "models/Major";
import { reportRepository } from "repositories/report-repository";
import { Organization, OrganizationFilter } from "models/Organization";
import { Subject, SubjectFilter } from "models/Subject";
import { Semester, SemesterFilter } from "models/Semester";
import { ReportFilter, Report } from "models/Report";
import { CourseSection, CourseSectionFilter } from "models/CourseSection";
import { formatNumber } from "helpers/number";
import { Moment } from "moment";
import { RollCallType } from "models/RollCallType";
import { AppUser, AppUserFilter } from "models/AppUser";
import StandardTable from "components/StandardTable";
import { Student } from "models/Student";
import AdvanceDateRangeFilterMaster from "components/AdvanceDateRangeFilter";
import TruncateCell from "components/TruncateCell";
import LayoutHeader from "components/LayoutHeader";
import LayoutCell from "components/LayoutCell";
import { formatDate } from "helpers/date-time";
import { EducationType, EducationTypeFilter } from "models/EducationType";
/* end individual import */

function ReportRollCallDetail() {
  const [translate] = useTranslation();
  const [modelFilter, dispatch] = queryStringService.useQueryString(
    ReportFilter,
    { skip: 0, take: 10 }
  );

  const {
    value: filter,
    handleChangeAllFilter,
    handleChangeSelectFilter,
    handleChangeDateFilter,
  } = filterService.useFilter(modelFilter, dispatch);

  const { list, count, loadingList } = listService.useList(
    reportRepository.listReportRollCallDetail,
    reportRepository.countReportRollCallDetail,
    filter,
    dispatch
  );

  const { handleTableChange, handlePagination } = tableService.useTable(
    filter,
    handleChangeAllFilter
  );

  const { loading: loadingExport, handleListExport } =
    importExportService.useExport();
  const pagination: PaginationProps = tableService.usePagination(filter, count);

  const columns: ColumnProps<Report>[] = useMemo(
    () => [
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("general.columns.index")}
            position="center"
          />
        ),
        key: "index",
        width: 60,
        render(...params: [CourseSection, Report, number]) {
          const index = renderMasterIndex<Report>(pagination)(...params);
          return (
            <LayoutCell orderType="left" tableSize="md" position="center">
              <OneLineText value={formatNumber(index)} />
            </LayoutCell>
          );
        },
      },
      {
        title: () => {
          return (
            <LayoutHeader
              orderType="left"
              title={translate("reports.students.code")}
            />
          );
        },
        key: nameof(list[0].student),
        dataIndex: nameof(list[0].student),
        width: "6%",
        render(...params: [Student, Report, number]) {
          return <TruncateCell renderContent={params[0]?.code} />;
        },
      },
      {
        title: () => {
          return (
            <LayoutHeader
              orderType="left"
              title={translate("reports.students.name")}
            />
          );
        },
        key: nameof(list[0].student),
        dataIndex: nameof(list[0].student),
        width: "8%",
        render(...params: [Student, Report, number]) {
          return <TruncateCell renderContent={params[0]?.name} />;
        },
      },
      {
        title: () => {
          return (
            <LayoutHeader
              orderType="left"
              title={translate("reports.className")}
            />
          );
        },
        key: nameof(list[0].className),
        dataIndex: nameof(list[0].className),
        width: "4%",
        render(...params: [string, Report, number]) {
          return <TruncateCell renderContent={params[0]} />;
        },
      },
      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "courseSection"
          );
          return (
            <LayoutHeader
              orderType="left"
              title={translate("reports.courseSections.code")}
              sortedColumn={sortedColumn}
              isSorter
            />
          );
        },
        key: nameof(list[0].courseSection),
        dataIndex: nameof(list[0].courseSection),
        sorter: true,
        sortOrder: getAntOrderType<Report, ReportFilter>(
          filter,
          nameof(list[0].courseSection)
        ),
        width: "7%",
        render(...params: [CourseSection, Report, number]) {
          return <TruncateCell renderContent={params[0]?.code} />;
        },
      },
      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "subject"
          );
          return (
            <LayoutHeader
              orderType="left"
              title={translate("reports.subject")}
              sortedColumn={sortedColumn}
              isSorter
            />
          );
        },
        key: nameof(list[0].subject),
        dataIndex: nameof(list[0].subject),
        sorter: true,
        sortOrder: getAntOrderType<Report, ReportFilter>(
          filter,
          nameof(list[0].subject)
        ),
        width: "9%",
        render(...params: [Subject, Report, number]) {
          return <TruncateCell renderContent={params[0]?.name} />;
        },
      },
      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "major"
          );
          return (
            <LayoutHeader
              orderType="left"
              title={translate("reports.major")}
              sortedColumn={sortedColumn}
              isSorter
            />
          );
        },
        key: nameof(list[0].major),
        dataIndex: nameof(list[0].major),
        sorter: true,
        sortOrder: getAntOrderType<Report, ReportFilter>(
          filter,
          nameof(list[0].major)
        ),
        width: "6%",
        render(...params: [Major, Report, number]) {
          return <TruncateCell renderContent={params[0]?.name} />;
        },
      },
      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "organization"
          );
          return (
            <LayoutHeader
              orderType="left"
              title={translate("reports.organization")}
              sortedColumn={sortedColumn}
              isSorter
            />
          );
        },
        key: nameof(list[0].organization),
        dataIndex: nameof(list[0].organization),
        sorter: true,
        sortOrder: getAntOrderType<Report, ReportFilter>(
          filter,
          nameof(list[0].organization)
        ),
        width: "7%",
        render(...params: [Organization, Report, number]) {
          return <TruncateCell renderContent={params[0]?.name} />;
        },
      },
      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "educationType"
          );
          return (
            <LayoutHeader
              orderType="left"
              title={"Hệ đào tạo"}
              sortedColumn={sortedColumn}
              isSorter
            />
          );
        },
        key: nameof(list[0].educationType),
        dataIndex: nameof(list[0].educationType),
        sorter: true,
        sortOrder: getAntOrderType<Report, ReportFilter>(
          filter,
          nameof(list[0].educationType)
        ),
        width: "5%",
        render(...params: [EducationType, Report, number]) {
          return <TruncateCell renderContent={params[0]?.name} />;
        },
      },
      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "semester"
          );
          return (
            <LayoutHeader
              orderType="left"
              title={translate("reports.semester")}
              sortedColumn={sortedColumn}
              isSorter
            />
          );
        },
        width: "6%",
        key: nameof(list[0].semester),
        dataIndex: nameof(list[0].semester),
        sorter: true,
        sortOrder: getAntOrderType<Report, ReportFilter>(
          filter,
          nameof(list[0].semester)
        ),
        render(...params: [Semester, Report, number]) {
          return <TruncateCell renderContent={params[0]?.name} />;
        },
      },
      {
        title: () => {
          return (
            <LayoutHeader
              orderType="left"
              title={translate("reports.appUserNames")}
            />
          );
        },
        key: nameof(list[0].appUserNames),
        dataIndex: nameof(list[0].appUserNames),
        render(...params: [string, Report, number]) {
          return <TruncateCell renderContent={params[0]} />;
        },
      },
      {
        title: () => {
          return (
            <LayoutHeader
              orderType="left"
              title={translate("reports.appUserCheckin")}
            />
          );
        },
        key: nameof(list[0].appUser),
        dataIndex: nameof(list[0].appUser),
        width: "8%",
        render(...params: [AppUser, Report, number]) {
          return <TruncateCell renderContent={params[0]?.displayName} />;
        },
      },
      {
        title: () => {
          return (
            <LayoutHeader
              orderType="left"
              title={translate("reports.lessonDate")}
              position="center"
            />
          );
        },
        key: nameof(list[0].lessonDate),
        dataIndex: nameof(list[0].lessonDate),
        width: 100,
        render(...params: [Moment, Report, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md" position="center">
              <OneLineText value={params[0] ? formatDate(params[0]) : ""} />
            </LayoutCell>
          );
        },
      },
      {
        title: () => {
          return (
            <LayoutHeader
              orderType="left"
              title={translate("reports.checkinAt")}
              position="center"
            />
          );
        },
        key: nameof(list[0].checkinAt),
        dataIndex: nameof(list[0].checkinAt),
        width: 100,
        render(...params: [Moment, Report, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md" position="center">
              <OneLineText value={params[0] ? formatDate(params[0]) : ""} />
            </LayoutCell>
          );
        },
      },
      {
        title: () => {
          return (
            <LayoutHeader
              orderType="left"
              title={translate("reports.indexOfLesson")}
              position="right"
            />
          );
        },
        key: nameof(list[0].indexOfLesson),
        dataIndex: nameof(list[0].indexOfLesson),
        width: "4%",
        render(...params: [number, Report, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md" position="right">
              <OneLineText value={formatNumber(params[0])} />
            </LayoutCell>
          );
        },
      },
      {
        title: () => {
          return (
            <LayoutHeader
              orderType="left"
              title={translate("reports.rollCallType")}
            />
          );
        },
        key: nameof(list[0].rollCallType),
        dataIndex: nameof(list[0].rollCallType),
        width: "5%",
        render(...params: [RollCallType, Report, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <OneLineText value={params[0]?.name} />
            </LayoutCell>
          );
        },
      },
    ],
    [translate, pagination, list, filter]
  );

  return (
    <>
      <Spin spinning={loadingExport || loadingList}>
        <div className="page-content">
          <PageHeader
            title={translate("reports.reportRollCallDetails.master.header")}
            breadcrumbItems={[
              translate("menu.title.report"),
              translate("reports.reportRollCallDetails.master.header"),
            ]}
          />

          <div className="page page-master m-l--sm m-r--xxl m-b--xxs">
            <div className="page-master__title p-l--sm p-t--xs p-b--xs">
              {translate("reports.reportRollCallDetails.master.header")}
            </div>
            <div className="page-master__content">
              <div className="page-master__tag-filter">
                <TagFilter
                  value={filter}
                  translate={translate}
                  keyTranslate={"reports"}
                  handleChangeFilter={handleChangeAllFilter}
                  onClear={() => {
                    return 0;
                  }}
                />
              </div>
              <div className="page-master__filter-wrapper d-flex align-items-center justify-content-between">
                <div className="page-master__filter d-flex align-items-center justify-content-start">
                  <div className="d-flex align-items-center">
                    <div className="">
                      <AdvanceIdFilterMaster
                        value={filter["semesterId"]["equal"]}
                        placeHolder={translate("reports.placeholder.semester")}
                        classFilter={SemesterFilter}
                        onChange={handleChangeSelectFilter({
                          fieldName: "semester",
                          fieldType: "equal",
                          classFilter: IdFilter,
                        })}
                        getList={reportRepository.filterListSemester}
                        title={translate("reports.semester")}
                      />
                    </div>
                    <div className="">
                      <AdvanceIdFilterMaster
                        value={filter["courseSectionId"]["equal"]}
                        placeHolder={translate(
                          "reports.placeholder.courseSection"
                        )}
                        classFilter={CourseSectionFilter}
                        onChange={handleChangeSelectFilter({
                          fieldName: "courseSection",
                          fieldType: "equal",
                          classFilter: IdFilter,
                        })}
                        getList={reportRepository.filterListCourseSection}
                        title={translate("reports.courseSection")}
                      />
                    </div>
                    <div className="">
                      <AdvanceIdFilterMaster
                        value={filter["subjectId"]["equal"]}
                        placeHolder={translate("reports.placeholder.subject")}
                        classFilter={SubjectFilter}
                        onChange={handleChangeSelectFilter({
                          fieldName: "subject",
                          fieldType: "equal",
                          classFilter: IdFilter,
                        })}
                        getList={reportRepository.filterListSubject}
                        title={translate("reports.subject")}
                      />
                    </div>
                    <div className="">
                      <AdvanceIdFilterMaster
                        value={filter["majorId"]["equal"]}
                        placeHolder={translate("reports.placeholder.major")}
                        classFilter={MajorFilter}
                        onChange={handleChangeSelectFilter({
                          fieldName: "major",
                          fieldType: "equal",
                          classFilter: IdFilter,
                        })}
                        getList={reportRepository.filterListMajor}
                        title={translate("reports.major")}
                      />
                    </div>
                    <div className="">
                      <AdvanceIdFilterMaster
                        value={filter["organizationId"]["equal"]}
                        placeHolder={translate(
                          "reports.placeholder.organization"
                        )}
                        classFilter={OrganizationFilter}
                        onChange={handleChangeSelectFilter({
                          fieldName: "organization",
                          fieldType: "equal",
                          classFilter: IdFilter,
                        })}
                        getList={reportRepository.filterListOrganization}
                        title={translate("reports.organization")}
                      />
                    </div>
                    <div className="">
                      <AdvanceIdFilterMaster
                        value={filter["educationTypeId"]["equal"]}
                        placeHolder={"Chọn hệ đào tạo"}
                        classFilter={EducationTypeFilter}
                        onChange={handleChangeSelectFilter({
                          fieldName: "educationType",
                          fieldType: "equal",
                          classFilter: IdFilter,
                        })}
                        getList={reportRepository.filterListEducationType}
                        title={"Hệ đào tạo"}
                      />
                    </div>
                    <div className="">
                      <AdvanceIdFilterMaster
                        value={filter["teacherId"]["equal"]}
                        placeHolder={translate("reports.placeholder.appUser")}
                        classFilter={AppUserFilter}
                        onChange={(id, value) => {
                          value.name = value?.displayName;
                          handleChangeSelectFilter({
                            fieldName: nameof(list[0].appUser),
                            fieldType: "equal",
                            classFilter: IdFilter,
                          })(id, value);
                        }}
                        getList={reportRepository.filterListAppUser}
                        title={translate("reports.appUser")}
                        render={(appUser: AppUser) => appUser?.displayName}
                        searchProperty="search"
                        searchType=""
                      />
                    </div>

                    <AdvanceDateRangeFilterMaster
                      onChange={(value?: [any, any]) => {
                        const internalValue =
                          !value[0] && !value[1] ? undefined : value;
                        handleChangeDateFilter({
                          fieldName: "date",
                          fieldType: ["greaterEqual", "lessEqual"],
                        })(internalValue);
                      }}
                      value={[
                        filter?.date?.greaterEqual as any,
                        filter?.date?.lessEqual as any,
                      ]}
                      placeholder={[
                        translate("general.filter.startDate"),
                        translate("general.filter.endDate"),
                      ]}
                      label={translate("reports.date")}
                    />
                  </div>
                </div>
                <div className="page-master__actions  d-flex align-items-center justify-content-start">
                  <div className="page-master__filter-action d-flex align-items-center">
                    <Button
                      type="primary"
                      icon={<Download16 />}
                      onClick={handleListExport(
                        filter,
                        reportRepository.exportReportRollCallDetail
                      )}
                      className="btn--lg"
                    >
                      {translate("reports.exportReportRollCallDetail")}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <div className="page-master__content-table">
              <StandardTable
                rowKey={nameof(list[0].key)}
                columns={columns}
                dataSource={list}
                isDragable={true}
                tableSize={"md"}
                onChange={handleTableChange}
                spinning={loadingList}
                bordered
                scroll={{ y: 500, }}
              />

              <Pagination
                skip={filter?.skip}
                take={filter?.take}
                total={count}
                onChange={handlePagination}
              />
            </div>
          </div>
        </div>
      </Spin>
    </>
  );
}
export default ReportRollCallDetail;
